import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Day pills for the "Jetzt passend" section.
 *
 * One line on every screen size: on a phone the row scrolls sideways, with the
 * same edge fades and the same one-off scroll hint as the Ausflugsideen filter
 * bar. The active day is filled, the others stay white with an emerald border.
 */
export default function DayChipsRow({ options = [], selectedKey, onSelect }) {
  const rowRef = useRef(null);
  const rafRef = useRef(null);
  const hintTimerRef = useRef(null);
  const [edges, setEdges] = useState({ scrolled: false, canScrollRight: false });
  const [hinted, setHinted] = useState(false);

  // One geometry read per pass: read everything first, then write once. Nothing
  // is measured again after the write — that is what forces a synchronous layout.
  const measure = () => {
    const el = rowRef.current;
    if (!el) return false;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    const next = {
      scrolled: scrollLeft > 4,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - 4,
    };
    setEdges((prev) =>
      prev.scrolled === next.scrolled && prev.canScrollRight === next.canScrollRight ? prev : next
    );
    return scrollWidth > clientWidth + 1;
  };

  // Measured in an animation frame, when the browser has already laid the row out.
  useEffect(() => {
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      // Hint only when the row really overflows — the fades are meaningless otherwise.
      if (measure()) hintTimerRef.current = setTimeout(() => setHinted(true), 600);
    });
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, [options]);

  // rAF-throttled: coalesce geometry reads into one per frame.
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
      setHinted(false);
    });
  };

  return (
    <div className="relative overflow-hidden mb-4">
      {edges.scrolled && (
        <div
          className="absolute left-0 top-0 bottom-0 w-4 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to right, #ffffff, transparent)' }}
        />
      )}
      {edges.canScrollRight && (
        <div
          className="absolute right-0 top-0 bottom-0 w-4 z-10 pointer-events-none"
          style={{ background: 'linear-gradient(to left, #ffffff, transparent)' }}
        />
      )}
      <div
        ref={rowRef}
        onScroll={handleScroll}
        className={cn('flex gap-2 overflow-x-auto scrollbar-hide', hinted && 'pills-hint')}
        style={{ flexWrap: 'nowrap' }}
      >
        {options.map((option) => {
          const active = option.key === selectedKey;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onSelect(option.key)}
              aria-pressed={active}
              className={cn(
                'flex-shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-emerald-900 text-white border-emerald-900'
                  : 'bg-white text-emerald-700 border-emerald-600 hover:bg-emerald-50'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}